/**
 * @fileoverview ContentSharingPanel — host controls for PDF screenshare content sharing.
 *
 * Feature-flagged: VITE_CONTENT_SHARING_ENABLED !== 'off'.
 * Shows a list of ready PDF attachments and controls to start/navigate/stop sharing.
 *
 * @module components/ContentSharingPanel
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMeetingAttachments, updateDocumentState, ApiError } from '../services/api';

interface ContentSharingPanelProps {
  meetingId: string;
  token: string;
}

interface AttachmentOption {
  id: string;
  filename: string;
  processing_status: string;
  page_count: number | null;
  content_type: string;
  screenshare_status: string;
}

export default function ContentSharingPanel({ meetingId, token }: ContentSharingPanelProps) {
  // Feature flag: VITE_CONTENT_SHARING_ENABLED !== 'off'
  const enabled = import.meta.env.VITE_CONTENT_SHARING_ENABLED !== 'off';
  if (!enabled) return null;

  return <ContentSharingPanelInner meetingId={meetingId} token={token} />;
}

function ContentSharingPanelInner({ meetingId, token }: ContentSharingPanelProps) {
  const [attachments, setAttachments] = useState<AttachmentOption[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isSharing, setIsSharing] = useState(false);
  const [jumpPage, setJumpPage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const loadAttachments = useCallback(async () => {
    if (!meetingId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const items = await fetchMeetingAttachments(meetingId, token);
      // Only show PDF attachments that are fully processed, have rendered pages, and are approved for sharing
      const ready = items.filter(
        (a) =>
          a.processing_status === 'processed' &&
          (a.page_count ?? 0) > 0 &&
          a.content_type === 'application/pdf' &&
          a.screenshare_status === 'approved_for_meeting_share',
      );
      setAttachments(ready);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setError(`Failed to load attachments: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [meetingId, token]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      void loadAttachments();
    }
  }, [loadAttachments]);

  const selectedAttachment = attachments.find((a) => a.id === selectedId) ?? null;
  const pageCount = selectedAttachment?.page_count ?? 0;

  const handleStartSharing = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    setError(null);
    try {
      await updateDocumentState(meetingId, { attachment_id: selectedId, page_number: 1, display_mode: 'content', is_manual_override: true }, token);
      setCurrentPage(1);
      setIsSharing(true);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setError(`Failed to start sharing: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [meetingId, selectedId, token]);

  const handleStopSharing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await updateDocumentState(meetingId, { page_number: currentPage || 1, display_mode: 'agenda' }, token);
      setIsSharing(false);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setError(`Failed to stop sharing: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [meetingId, currentPage, token]);

  const navigatePage = useCallback(async (newPage: number) => {
    if (!selectedId || newPage < 1 || newPage > pageCount) return;
    setLoading(true);
    setError(null);
    try {
      await updateDocumentState(meetingId, { attachment_id: selectedId, page_number: newPage, display_mode: 'content', is_manual_override: true }, token);
      setCurrentPage(newPage);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setError(`Failed to navigate: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [meetingId, selectedId, pageCount, token]);

  const handleJump = useCallback(async () => {
    const n = parseInt(jumpPage, 10);
    if (!Number.isNaN(n)) {
      await navigatePage(n);
      setJumpPage('');
    }
  }, [jumpPage, navigatePage]);

  if (attachments.length === 0 && !loading && !error) {
    return (
      <div className="content-sharing-panel content-sharing-panel--empty">
        <span className="content-sharing-panel__label">No ready PDF attachments for sharing.</span>
        <button className="content-sharing-panel__btn" onClick={() => void loadAttachments()} disabled={loading}>
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="content-sharing-panel">
      <div className="content-sharing-panel__header">
        <span className="content-sharing-panel__title">Content Sharing</span>
        <button className="content-sharing-panel__btn content-sharing-panel__btn--sm" onClick={() => void loadAttachments()} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="content-sharing-panel__error">{error}</div>
      )}

      {!isSharing && (
        <div className="content-sharing-panel__select-row">
          <select
            className="content-sharing-panel__select"
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value || null)}
            disabled={loading}
          >
            <option value="">— Select a PDF —</option>
            {attachments.map((a) => (
              <option key={a.id} value={a.id}>
                {a.filename} ({a.page_count} pages)
              </option>
            ))}
          </select>
          <button
            className="content-sharing-panel__btn content-sharing-panel__btn--primary"
            onClick={() => void handleStartSharing()}
            disabled={!selectedId || loading}
          >
            {loading ? 'Loading…' : 'Share (p.1)'}
          </button>
        </div>
      )}

      {isSharing && (
        <div className="content-sharing-panel__controls">
          <span className="content-sharing-panel__status">
            Sharing: <strong>{selectedAttachment?.filename ?? ''}</strong>
          </span>
          <div className="content-sharing-panel__nav">
            <button
              className="content-sharing-panel__btn"
              onClick={() => void navigatePage(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
            >
              ← Prev
            </button>
            <span className="content-sharing-panel__page">
              {currentPage} / {pageCount}
            </span>
            <button
              className="content-sharing-panel__btn"
              onClick={() => void navigatePage(currentPage + 1)}
              disabled={currentPage >= pageCount || loading}
            >
              Next →
            </button>
          </div>
          <div className="content-sharing-panel__jump">
            <input
              type="number"
              className="content-sharing-panel__jump-input"
              value={jumpPage}
              min={1}
              max={pageCount}
              onChange={(e) => setJumpPage(e.target.value)}
              placeholder="Page #"
              disabled={loading}
            />
            <button
              className="content-sharing-panel__btn"
              onClick={() => void handleJump()}
              disabled={!jumpPage || loading}
            >
              Go
            </button>
          </div>
          <button
            className="content-sharing-panel__btn content-sharing-panel__btn--danger"
            onClick={() => void handleStopSharing()}
            disabled={loading}
          >
            {loading ? 'Stopping…' : 'Stop Sharing'}
          </button>
        </div>
      )}
    </div>
  );
}
